import { Component, ElementRef, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { NotificationService } from '../../../../services/notification.service';
import { ToastService } from '../../../../services/toast.service';
import { NotificationSocketService } from '../../../../services/notification-socket-service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [DatePipe, CommonModule, RouterModule],
  templateUrl: './notification.html',
  styleUrl: './notification.scss'
})

export class Notification implements OnInit, OnDestroy {
  notifications: any[] = [];
  isDropdownOpen: Boolean = false;
  count: number = 0;
  unreadCount: number = 0;
  currentPage: number = 1;
  pageSize: number = 5;
  totalPages: number = 0;
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  hasMorePages: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private eRef: ElementRef,
    private notificationService: NotificationService,
    private router: Router,
    private toastService: ToastService,
    private notificationSocketService: NotificationSocketService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (!window.location.pathname.includes('/admin')) {
      this.getNotifications();

      // Subscribe to socket notification refresh events
      this.notificationSocketService.refreshNotifications$
        .pipe(takeUntil(this.destroy$))
        .subscribe((notification: any) => {
          if (notification) {
            this.count = (this.count || 0) + 1;
            if (notification.status_id === 2) {
              this.unreadCount = (this.unreadCount || 0) + 1;
            }
            this.getNotifications(1, false);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  handleClickOutside(event: MouseEvent) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }
  getNotifications(page: number = 1, append: boolean = false) {
    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
    }

    const params = {
      limit: this.pageSize,
      page_no: page
    };

    this.notificationService.getAllNotifications(params).subscribe({
      next: (res: any) => {
        const newNotifications = res?.data.rows || [];
        if (append) {
          this.notifications = [...this.notifications, ...newNotifications];
        } else {
          this.notifications = newNotifications;
        }
        this.count = res?.data.count || 0;
        this.unreadCount = res?.data.unreadCount || 0;
        this.totalPages = Math.ceil(this.count / this.pageSize);
        this.currentPage = page;
        this.hasMorePages = page < this.totalPages;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: () => {
        if (!append) {
          this.notifications = [];
        }
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    const threshold = 50;

    if (scrollHeight - scrollTop - clientHeight < threshold && 
        this.hasMorePages && 
        !this.isLoadingMore && 
        !this.isLoading) {
      this.loadMoreNotifications();
    }
  }

  loadMoreNotifications() {
    if (this.currentPage < this.totalPages) {
      this.getNotifications(this.currentPage + 1, true);
    }
  }

  updateNotificationStatus(status: boolean) {
    this.isDropdownOpen = status;
  }

  notificationNavigation(event: any, notification: any, navigate: boolean = false) {
    event.stopPropagation();

    if (notification.status_id === 2) {
      this.notificationService.updateNotificationStatus({ id: notification.id, status_id: 1 }).subscribe({
        next: () => {
          notification.status_id = 1;
          notification.status = 'Read';
          this.count = Math.max(0, this.count - 1);
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        },
        error: () => {
          this.toastService.showToast({
            type: 'error',
            summary: 'Error',
            detail: 'Failed to update notification status'
          });
        }
      });
    }

    if (navigate && notification.action) {
      try {
        const action = typeof notification.action === 'string' 
          ? JSON.parse(notification.action) 
          : notification.action;
        if (action.key === 'form_id' && action.value) {
          const currentUrl = this.router.url;
          const targetPath = `/create-new-form/form-detail/${action.value}`;
          
          if (currentUrl.startsWith('/create-new-form/form-detail/')) {
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this.router.navigateByUrl(`${targetPath}?viewMode=true`);
            });
          } else {
            this.router.navigateByUrl(`${targetPath}?viewMode=true`);
          }
        }
      } catch (error) {
        console.error('Failed to parse notification action:', error);
      }
    }
  }

  markAllAsRead(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.notificationService.markAllNotificationsAsRead().subscribe({
      next: (res: any) => {
        this.getNotifications(1, false);
        this.toastService.showToast({
          type: 'success',
          summary: 'Success',
          detail: res?.message || 'All notifications marked as read'
        });
      },
      error: () => {
        this.toastService.showToast({
          type: 'error',
          summary: 'Error',
          detail: 'Failed to mark all notifications as read'
        });
      }
    });
  }
}